// Ítems de navegación del panel (design-system §5.1).
// Fuente única para los tres patrones de navegación del mismo código:
//   - escritorio (>= 1280 px): sidebar oscuro con todos los ítems (maqueta 04);
//   - tablet (768–1279 px): riel lateral de iconos con etiqueta (mismos ítems);
//   - móvil (< 768 px): barra de pestañas inferior Inicio · Chats · Pedidos · Inventario · Más.
// Módulo puro (sin React) para poder probarlo con Vitest en entorno node.

/** Identificador del icono SVG de trazo que pinta cada ítem (components/nav-icon.tsx). */
export type NavIconName = 'home' | 'chats' | 'orders' | 'inventory' | 'payments' | 'train' | 'more';

export interface NavItem {
  /** Identificador estable (en inglés) del ítem. */
  id: string;
  /** Etiqueta en el sidebar y el riel (escritorio y tablet). */
  label: string;
  /** Etiqueta corta en la barra de pestañas móvil. */
  shortLabel: string;
  href: string;
  icon: NavIconName;
  /**
   * "primary": aparece también como pestaña en móvil.
   * "secondary": solo en sidebar/riel; en móvil se agrupa bajo "Más".
   */
  placement: 'primary' | 'secondary';
}

/** Ítems del panel, en el orden del sidebar de la maqueta 04. */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'home',
    label: 'Resumen',
    shortLabel: 'Inicio',
    href: '/',
    icon: 'home',
    placement: 'primary',
  },
  {
    id: 'chats',
    label: 'Conversaciones',
    shortLabel: 'Chats',
    href: '/conversaciones',
    icon: 'chats',
    placement: 'primary',
  },
  {
    id: 'orders',
    label: 'Pedidos',
    shortLabel: 'Pedidos',
    href: '/pedidos',
    icon: 'orders',
    placement: 'primary',
  },
  {
    id: 'inventory',
    label: 'Inventario',
    shortLabel: 'Inventario',
    href: '/inventario',
    icon: 'inventory',
    placement: 'primary',
  },
  {
    id: 'payments',
    label: 'Pagos',
    shortLabel: 'Pagos',
    href: '/pagos',
    icon: 'payments',
    placement: 'secondary',
  },
  {
    id: 'train',
    label: 'Entrenar agente',
    shortLabel: 'Entrenar',
    href: '/entrenar',
    icon: 'train',
    placement: 'secondary',
  },
];

/** Pestaña "Más" de la barra inferior: reúne los ítems secundarios en móvil. */
export const MORE_TAB: NavItem = {
  id: 'more',
  label: 'Más',
  shortLabel: 'Más',
  href: '/mas',
  icon: 'more',
  placement: 'primary',
};

/** Ítems del sidebar (escritorio) y del riel (tablet). */
export function getSidebarItems(items: readonly NavItem[] = NAV_ITEMS): NavItem[] {
  return [...items];
}

/** Pestañas de la barra inferior móvil: los ítems primarios y, al final, "Más". */
export function getTabBarItems(items: readonly NavItem[] = NAV_ITEMS): NavItem[] {
  return [...items.filter((item) => item.placement === 'primary'), MORE_TAB];
}

/** Quita la barra final, la query y el fragmento: "/pedidos/?a=1#x" → "/pedidos". */
function normalizePath(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0] ?? '';
  const trimmed = path.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/**
 * ¿La ruta actual pertenece a este href? La raíz "/" solo coincide exactamente;
 * el resto coincide consigo mismo y con sus subrutas ("/pedidos/123"), nunca con
 * prefijos parciales ("/pedidosx").
 */
export function isNavItemActive(pathname: string, href: string): boolean {
  const current = normalizePath(pathname);
  const target = normalizePath(href);
  if (target === '/') return current === '/';
  return current === target || current.startsWith(`${target}/`);
}

/**
 * Pestaña móvil activa para la ruta actual: la pestaña primaria que coincide; si la ruta es
 * de un ítem secundario (o de la propia página "Más"), la pestaña "Más". Sin coincidencia: null.
 */
export function getActiveTabId(
  pathname: string,
  items: readonly NavItem[] = NAV_ITEMS,
): string | null {
  const primary = items.find(
    (item) => item.placement === 'primary' && isNavItemActive(pathname, item.href),
  );
  if (primary) return primary.id;
  const inMore =
    isNavItemActive(pathname, MORE_TAB.href) ||
    items.some((item) => item.placement === 'secondary' && isNavItemActive(pathname, item.href));
  return inMore ? MORE_TAB.id : null;
}
